// src/pages/documents/InvoiceListPage/InvoiceListPage.jsx
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import OrdersAPI from '../../../api/v3/orders-api-v3';

const API_BASE = process.env.REACT_APP_API_BASE;
const fmtIDR = (n) => `Rp ${Number(n || 0).toLocaleString('id-ID')}`;

const normalizeOrderStatus = (status) => (status || '').toString().trim().toLowerCase();

function invoiceStatusBadgeMeta(row) {
  const status = normalizeOrderStatus(row?.status).replace(/\s+/g, '_').replace(/-/g, '_');

  if (status === 'refunded') {
    return { label: 'Paid Full / Refunded', className: 'bg-secondary' };
  }
  if (status === 'returned') {
    return { label: 'Paid Full / Returned', className: 'bg-secondary' };
  }
  if (status === 'completed' || status === 'complete') {
    return { label: 'Paid Full / Completed', className: 'bg-success' };
  }

  return {
    label: 'Paid Full',
    className: 'bg-success'
  };
}

export default function InvoiceListPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  // filters (mirror TandaMasuk)
  const [query, setQuery] = useState('');
  const [voucherCode, setVoucherCode] = useState('');
  const [hasVoucherOnly, setHasVoucherOnly] = useState(false);

  // pagination
  const [page, setPage] = useState(1);
  const [per, setPer] = useState(25);

  const [selectedIds, setSelectedIds] = useState([]);
  const [downloading, setDownloading] = useState(null);
  const [bulkDownloading, setBulkDownloading] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setErr('');
      const params = { paid_full_only: '1', per: 100 };
      if (query) params.q = query;
      if (voucherCode) params.voucher_code = voucherCode;

      let all = [];
      let apiPage = 1;
      let apiTotalPages = 1;

      do {
        const json = await OrdersAPI.list({ ...params, page: apiPage });
        const data = json?.items || json?.data || json || [];
        all = all.concat(Array.isArray(data) ? data : []);
        apiTotalPages = Number(json?.meta?.total_pages || 1);
        apiPage += 1;
      } while (apiPage <= apiTotalPages);

      setRows(all);
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [query, voucherCode]);

  useEffect(() => { load(); }, [load]);

  // reset page when filters change
  useEffect(() => { setPage(1); }, [query, voucherCode, hasVoucherOnly]);

  const filtered = useMemo(() => {
    return rows.filter(r => {
      const q = (query || '').trim().toLowerCase();
      const matchesQuery = !q ||
        r.order_no?.toLowerCase().includes(q) ||
        r.customer?.name?.toLowerCase().includes(q) ||
        r.voucher?.code?.toLowerCase().includes(q);
      const matchesHasVoucher = !hasVoucherOnly || !!r.voucher;
      const matchesVoucherCode = !voucherCode ||
        r.voucher?.code?.toLowerCase().includes(voucherCode.toLowerCase());
      return matchesQuery && matchesHasVoucher && matchesVoucherCode;
    });
  }, [rows, query, voucherCode, hasVoucherOnly]);

  // pagination slices
  const totalPages = Math.max(1, Math.ceil(filtered.length / per));
  const pageSafe = Math.min(page, totalPages);
  const startIdx = (pageSafe - 1) * per;
  const paged = useMemo(() => filtered.slice(startIdx, startIdx + per), [filtered, startIdx, per]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAllVisible = (checked) => {
    const visibleIds = paged.map(r => r.id);
    setSelectedIds(prev => {
      if (checked) {
        const set = new Set([...prev, ...visibleIds]);
        return Array.from(set);
      } else {
        return prev.filter(id => !visibleIds.includes(id));
      }
    });
  };

  async function onPrint(id) {
    setDownloading(id);
    try {
      const url = `${API_BASE}/api/v3/orders/${id}/invoice`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Failed to generate invoice for ID ${id}`);
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = window.URL.createObjectURL(blob);
      a.download = `Invoice_${id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(a.href);
    } catch (e) {
      alert(e.message);
    } finally {
      setDownloading(null);
    }
  }

  async function onBulkPrint() {
    if (!selectedIds.length) return alert('Pilih minimal 1 order.');
    setBulkDownloading(true);
    try {
      const url = `${API_BASE}/api/v3/orders/bulk_invoices?ids=${selectedIds.join(',')}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error('Gagal membuat PDF gabungan.');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = window.URL.createObjectURL(blob);
      a.download = `Invoices_${new Date().toISOString().slice(0,16).replace(/[:T]/g,'')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(a.href);
    } catch (e) {
      alert(e.message);
    } finally {
      setBulkDownloading(false);
    }
  }

  return (
    <div className="bagspa-page invoices-page">
      <div className="bagspa-page-header invoices-page-header">
        <div>
          <h3 className="mb-0">Invoices</h3>
          <div className="small text-muted">
            Paid Full archive only. Paid DP is not included.
          </div>
        </div>
      </div>

      <div className="bagspa-filter-card invoices-control-card">
        <div className="invoices-control-row">
          <div className="bagspa-filter-field invoices-filter-search">
            <label className="form-label">Search</label>
            <input
              type="text"
              className="form-control"
              placeholder="Cari nama customer / order no / voucher code..."
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          </div>

          <div className="bagspa-filter-field invoices-filter-voucher">
            <label className="form-label">Voucher</label>
            <input
              type="text"
              className="form-control"
              placeholder="Filter voucher code"
              value={voucherCode}
              onChange={e => setVoucherCode(e.target.value)}
            />
          </div>

          <div className="bagspa-filter-field invoices-filter-has-voucher">
            <label className="form-label">Options</label>
            <div className="invoices-check-row">
              <div className="form-check">
                <input
                  className="form-check-input"
                  id="inv-has-voucher"
                  type="checkbox"
                  checked={hasVoucherOnly}
                  onChange={e => setHasVoucherOnly(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="inv-has-voucher">
                  Has voucher only
                </label>
              </div>
            </div>
          </div>

          <div className="bagspa-filter-field invoices-filter-rows">
            <label className="form-label">Rows</label>
            <select
              className="form-control"
              value={per}
              onChange={(e) => {
                setPer(parseInt(e.target.value, 10) || 25);
                setPage(1);
              }}
            >
              {[10, 25, 50, 100].map(n => (
                <option key={n} value={n}>
                  {n}/page
                </option>
              ))}
            </select>
          </div>

          <div className="invoices-filter-apply">
            <button className="btn btn-primary" onClick={load}>
              Apply
            </button>
          </div>

          <div className="invoices-filter-download">
            <button
              className="btn btn-success"
              disabled={!selectedIds.length || bulkDownloading}
              onClick={onBulkPrint}
              title="Gabungkan semua Invoice terpilih ke satu PDF"
            >
              {bulkDownloading ? 'Membuat PDF…' : `Download Selected (${selectedIds.length})`}
            </button>
          </div>
        </div>
      </div>

      {err && <div className="alert alert-danger">{err}</div>}

      {loading ? (
        <div>Loading…</div>
      ) : (
        <div className="bagspa-table-card invoices-table-card">
          <div className="table-responsive">
            <table className="table table-sm table-bordered align-middle invoices-table">
              <thead className="table-light">
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={paged.length > 0 && paged.every(r => selectedIds.includes(r.id))}
                      onChange={e => toggleSelectAllVisible(e.target.checked)}
                    />
                  </th>
                  <th>No</th>
                  <th>Order No</th>
                  <th>Status</th>
                  <th>Voucher</th>
                  <th>Customer</th>
                  <th>Layanan</th>
                  <th>Advance</th>
                  <th>Final</th>
                  <th>Outstanding</th>
                  <th>Tanggal</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {paged.length ? paged.map((r, i) => {
                  const adv = Number(r.advance_payment_price || 0);
                  const fin = Number(r.final_fixed_price || 0);
                  const outstanding = Math.max(fin - adv, 0);
                  const paid = outstanding <= 0;
                  const statusBadge = invoiceStatusBadgeMeta(r);

                  return (
                    <tr key={r.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(r.id)}
                          onChange={() => toggleSelect(r.id)}
                        />
                      </td>

                      <td>{startIdx + i + 1}</td>
                      <td>{r.order_no}</td>

                      <td>
                        <span className={`badge ${statusBadge.className}`}>
                          {statusBadge.label}
                        </span>
                      </td>

                      <td>
                        {r.voucher ? (
                          <>
                            <span className="badge bg-info-subtle text-info-emphasis">
                              {r.voucher.code}
                            </span>
                            <div className="small text-muted">
                              - {fmtIDR(r.voucher.discount_amount)}
                            </div>
                            {r.voucher.redeemed_at && (
                              <div className="small text-muted">
                                {dayjs(r.voucher.redeemed_at).format('DD MMM YYYY HH:mm')}
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>

                      <td>{r.customer?.name || '-'}</td>
                      <td>{r.order_category_name || '—'}</td>
                      <td>{fmtIDR(adv)}</td>
                      <td>{fmtIDR(fin)}</td>
                      <td className={paid ? '' : 'text-danger'}>
                        {fmtIDR(outstanding)}
                      </td>
                      <td>{r.created_at ? dayjs(r.created_at).format('DD/MM/YYYY') : '—'}</td>

                      <td>
                        <div className="invoices-row-actions">
                          <button
                            className="btn btn-sm btn-outline-success"
                            onClick={() => onPrint(r.id)}
                            disabled={downloading === r.id}
                          >
                            {downloading === r.id ? 'Downloading…' : 'PDF'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                }) : (
                  <tr>
                    <td colSpan={12} className="text-center text-muted">
                      Tidak ada data.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="bagspa-pagination-row invoices-pagination-row">
            <div className="text-muted small">
              Total: {filtered.length} • Page {pageSafe} / {totalPages}
            </div>

            <div className="btn-group">
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={pageSafe <= 1}
                onClick={() => setPage(1)}
              >
                &laquo;
              </button>

              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={pageSafe <= 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
              >
                &lsaquo;
              </button>

              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={pageSafe >= totalPages}
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              >
                &rsaquo;
              </button>

              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={pageSafe >= totalPages}
                onClick={() => setPage(totalPages)}
              >
                &raquo;
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
